import { useQuery } from "@tanstack/react-query";
import { analyticsServicesService } from "@/services/analytics-services-service";
import type { ServicesCategoryApi } from "@/types/analytics-services";
import type { DateRange } from "@/hooks/useDateRange";
import type { ServiceCategoryFilter } from "../components/services/mockData";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";

const CATEGORY_TO_API: Record<ServiceCategoryFilter, ServicesCategoryApi> = {
  all: "ALL",
  guestServices: "GUEST_SERVICES",
  activities: "ACTIVITIES",
  externalServices: "EXTERNAL_SERVICES",
  guesteriaPartners: "GUESTERIA_PARTNERS",
};

export function mapServiceCategoryToApi(
  category: ServiceCategoryFilter,
): ServicesCategoryApi {
  return CATEGORY_TO_API[category];
}

export const analyticsServicesKeys = {
  all: ["analytics-services"] as const,
  list: (
    hotelId: string,
    from: string,
    to: string,
    category: ServicesCategoryApi,
  ) =>
    [
      ...analyticsServicesKeys.all,
      "list",
      hotelId,
      from,
      to,
      category,
      1,
      5,
    ] as const,
  usage: (
    hotelId: string,
    from: string,
    to: string,
    category: ServicesCategoryApi,
  ) =>
    [
      ...analyticsServicesKeys.all,
      "usage",
      hotelId,
      from,
      to,
      category,
    ] as const,
};

export function useServicesAnalytics(
  dateRange: DateRange,
  category: ServiceCategoryFilter,
) {
  const rangeParams = useAnalyticsOverviewRange(dateRange);
  const { hotelId, from, to, isReady } = rangeParams;
  const apiCategory = mapServiceCategoryToApi(category);

  const list = useQuery({
    queryKey: analyticsServicesKeys.list(hotelId, from, to, apiCategory),
    queryFn: () =>
      analyticsServicesService.getList({
        hotelId,
        from,
        to,
        category: apiCategory,
        page: 1,
        pageSize: 5,
      }),
    enabled: isReady,
  });

  const usage = useQuery({
    queryKey: analyticsServicesKeys.usage(hotelId, from, to, apiCategory),
    queryFn: () =>
      analyticsServicesService.getUsage({
        hotelId,
        from,
        to,
        category: apiCategory,
      }),
    enabled: isReady,
  });

  return {
    ...rangeParams,
    apiCategory,
    list,
    usage,
    isLoading: list.isLoading || usage.isLoading,
    isFetching: list.isFetching || usage.isFetching,
    isError: list.isError || usage.isError,
    error: list.error ?? usage.error,
  };
}
